import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import { FortnoxContext } from '../../../context/FortnoxContext'
import { Body1, Body2, H6Bold } from '../../../components/Typography'
import { Loader } from '../../../components/Loader'
import { FlexingBox } from '../../../components/Flex'
import { spacing } from '../../../theme'
import { JobContext } from '../../../context/JobContext'
import { Role, useMeQuery, useUsersQuery } from '../../../graphql/generated'
import { GreenLink } from '../../../components/Link'
import { ErrorSnackBarContext } from '../../../context/ErrorSnackBarContex'
import { captureException } from '../../../utils/Sentry'
import { AccountingPrefNotConfigured } from '../AccountingPrefNotConfigured'

const Container = styled(FlexingBox)`
  max-width: 680px;
  padding: ${spacing.large};
  gap: ${spacing.small};
  min-height: auto;
  min-width: 65%;
`

export function FortnoxNoDataView(): React.JSX.Element {
  const { data: meData } = useMeQuery() // this is called inside AppDataContext which will handle the error
  const user = meData?.me
  const { jobRunning } = useContext(JobContext)
  const { permissionError } = useContext(FortnoxContext)
  const {
    data: usersData,
    error: userDataError,
    loading: userDataLoading,
  } = useUsersQuery({
    variables: {},
    skip: ![Role.Admin, Role.SysAdmin].includes(user?.role ?? Role.User),
  })

  const { setError } = useContext(ErrorSnackBarContext)
  useEffect(() => {
    if (userDataError) {
      captureException(userDataError)
      setError()
    }
  }, [userDataError, setError])

  const hasInvitedAccountants = !!usersData?.users.find(
    (u) => u.invited && [Role.Admin, Role.Accountant].includes(u.role),
  )

  if (jobRunning) {
    if (user?.role === Role.Accountant) {
      return (
        <Container>
          <CircularProgress />
          <H6Bold>Vi hämtar och analyserar er data...</H6Bold>
          <Body2 data-testid='accountant-working'>
            Tack! Nu har du godkänt hämtningen av data och vi har påbörjat
            beräkningarna. Processen kan ta olika lång tid beroende på hur stor
            verksamheten är. Vi meddelar dig via e-post när det är klart!
          </Body2>
          <Body2>
            Det går bra att lämna sidan, analysen kommer ändå fortsätta.
          </Body2>
        </Container>
      )
    } else {
      return (
        <Container>
          <CircularProgress />
          <H6Bold>Vi hämtar och analyserar er data...</H6Bold>
          <Body2 data-testid='working'>
            Processen kan ta olika lång tid beroende på hur stor verksamheten
            är. Passa på att ta en paus under tiden - vi meddelar dig via e-post
            när det är klart!
          </Body2>
        </Container>
      )
    }
  }
  if (
    [Role.Admin, Role.SysAdmin].includes(user?.role ?? Role.User) &&
    !userDataLoading
  ) {
    if (permissionError?.hasBeenSet) {
      if (permissionError.error) {
        // the user does not have the permissions to get the data from fortnox
        if (hasInvitedAccountants) {
          // there is an accountant (it can also be an admin) that has not logged in to the CIS yet
          return (
            <Container>
              <H6Bold data-testId='no-permission-no-accountant'>
                Vi väntar på godkännande för att kunna påbörja analysen...
              </H6Bold>
              <Body2>
                Personen med bokföringslicens i Fortnox (oftast en
                ekonomiadministratör eller revisor) har inte loggat in och
                godkänt villkoren än. Först när det är gjort kan vi hämta data
                och påbörja analysen. Processen kan ta olika lång tid beroende
                på hur stor verksamheten är. Vi meddelar dig via e-post när
                resultatet är klart!
              </Body2>
              <br />
              <Body2>
                <span>
                  För mer information eller support - Läs om behörigheter i vår
                </span>{' '}
                <GreenLink
                  href='https://svalna.gitlab.io/business-intelligence-frontend/permissions'
                  target='_blank'
                  rel='noreferrer'
                >
                  dokumentation
                </GreenLink>
                <span>, eller kontakta oss på</span>{' '}
                <GreenLink href='mailto:support@svalna.se'>
                  support@svalna.se
                </GreenLink>
                <span>.</span>
              </Body2>
            </Container>
          )
        }
        // all the accountants and admins have logged in but we still don't have the data and no job is running
        return (
          <Container>
            <H6Bold data-testId='no-permission-admin'>
              Vi väntar på godkännande för att kunna påbörja analysen...
            </H6Bold>
            <Body2>
              Det verkar som att du eller någon annan person med
              bokföringslicens i Fortnox inte har haft rätt inställningar
              aktiverade vid inloggning i vårt system. Vi saknar därför ert
              godkännande för att kunna hämta data och påbörja analysen.
            </Body2>
            <Body2>
              <span>Läs mer om behörighet och inställningar i vår</span>{' '}
              <GreenLink
                href='https://svalna.gitlab.io/business-intelligence-frontend/permissions'
                target='_blank'
                rel='noreferrer'
              >
                dokumentation
              </GreenLink>
              <span>
                , och kontrollera inställningarna på ert Fortnox konto. Kom
                sedan tillbaka och uppdatera sidan för att försöka på nytt.
              </span>
            </Body2>
            <br />
            <Body2>
              <span>För mer support - kontakta oss på</span>{' '}
              <a href='mailto:support@svalna.se'>support@svalna.se</a>
              <span>.</span>
            </Body2>
          </Container>
        )
      }
      if (!user?.organisation.hasAccountingConf) {
        return <AccountingPrefNotConfigured />
      }
      return (
        <Body1>
          Vi kommer att börja ladda ner och behandla dina uppgifter inom kort.
        </Body1>
      )
    }
  } else if (
    Role.Accountant === user?.role &&
    !user.organisation.hasAccountingConf
  ) {
    return <AccountingPrefNotConfigured />
  } else if (user) {
    // if it is not an admin the user can't load the data from Fortnox
    return (
      <Container>
        <H6Bold data-testid='no-permission-user'>
          Vi väntar på godkännande för att kunna påbörja analysen...
        </H6Bold>
        <Body2>
          En person med bokföringslicens i Fortnox behöver godkänna villkoren.
          Först när det är gjort kan vi hämta data och påbörja analysen.
          Processen kan ta olika lång tid beroende på hur stor verksamheten är.
          Vi meddelar dig via e-post när resultatet är klart!
        </Body2>
        <br />
        <Body2>
          <span>För mer support - kontakta oss på</span>{' '}
          <a href='mailto:support@svalna.se'>support@svalna.se</a>
          <span>.</span>
        </Body2>
      </Container>
    )
  }
  // If user or permissionError is not set yet just show a loading page
  return <Loader size={80} borderSize={20} />
}
